import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { captureException } from '@sentry/react';

import { DISABLE_UPLOAD, EMPTY_OBJECT, UPLOAD_API_BASE_URL } from '/src/constants';
import { UploadContext } from '/src/constants/contexts';
import useFetchedData from '/src/hooks/useFetchedData';
import useHttpClient from '/src/hooks/useHttpClient';
import useIsDesktop from '/src/hooks/useIsDesktop';
import { Alert, App } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import UploadBox from './UploadBox';
import Uploader, { TAB_ID } from './uploader';

function UploadProvider({ children }) {
  const [file, setFile] = useState();
  const [video, setVideo] = useState(null);
  const [uploadState, setUploadState] = useState();
  const { message, modal } = App.useApp();
  const httpClient = useHttpClient({ baseURL: UPLOAD_API_BASE_URL });
  const uploaderRef = useRef(null);
  const [progressPercent, setProgressPercent] = useState(0);
  const isUploading = uploadState === 'uploading';
  const uploadingVideo = Uploader.useUploadingVideo();
  const isActiveInAnotherTab = Boolean(
    uploadingVideo && uploadingVideo.id === video?.id && uploadingVideo.tabId !== TAB_ID,
  );
  const progress = (isActiveInAnotherTab ? uploadingVideo.progress : progressPercent) || 0;
  const isUploaded = useMemo(() => progress === 100 ||
  video?.fileState === 'UPLOADED' ||
  video?.state === 'PUBLISHED' ||
    (video?.s3?.parts && Object.values(video.s3.parts).every((p) => p.ETag)), [progress, video]);

  useEffect(() => {
    if (isUploading || !video) {
      return;
    }
    function calculateInitialProgress(video) {
      if (!video) {
        return 0;
      }
      return 100 * (video.s3?.parts ? Object.values(video.s3.parts).filter((p) => p.ETag).length / Object.keys(video.s3.parts).length : 0);
    }
    setProgressPercent(calculateInitialProgress(video));
  }, [isUploading, video]);

  const uploadDisabled = DISABLE_UPLOAD;

  const { data: status, loading } = useFetchedData(
    uploadDisabled ? null : {
      url: '/videos/status',
      baseURL: UPLOAD_API_BASE_URL,
    },
    ({ data } = EMPTY_OBJECT) => data?.status,
  );

  const killUpload = useCallback(() => {
    setFile(null);
    if (uploaderRef.current) {
      uploaderRef.current.destroy();
      uploaderRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!file || !video?.id) {
      return;
    }

    if (!uploaderRef.current) {
      try {
        uploaderRef.current = new Uploader({
          file,
          video,
          onVideoChange: setVideo,
          onClearFile: () => setFile(null),
          onUploadStateChange: setUploadState,
          onProgressChange: (progress) => {
            setProgressPercent(progress);
            if (progress === 100) {
              killUpload();
            }
          },
          httpClient,
          message,
          modal,
        });

        uploaderRef.current.start().catch((error) => {
          console.error('[UploadProvider] Error starting upload:', error);
          const uploadError = error?.response?.data?.error;
          if (uploadError === 'UPLOAD_DISABLED') {
            message.error('آپلود ویدیو برای حساب شما غیرفعال شده است.');
          } else if (uploadError === 'UPLOAD_QUOTA_EXCEEDED') {
            message.error('سهمیه آپلود شما برای این دوره به پایان رسیده است.');
          } else {
            captureException(error);
            message.error('خطا هنگام آپلود!');
          }
          killUpload();
        });
      } catch (error) {
        console.error('[UploadProvider] Error starting upload:', error);
        message.error(error.message);
        killUpload();
      }
    }
  }, [file, video]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles.length !== 1) {
        return;
      }

      const newFile = acceptedFiles[0];
      if (
        !newFile.type.startsWith('video/') &&
        !newFile.type.startsWith('audio/')
      ) {
        message.error('فایل انتخاب شده باید ویدیو یا صدا باشد!');
        return;
      }

      setFile(newFile);

      // if (!video) {
      //   httpClient
      //     .post('/videos', { filename: newFile.name })
      //     .then(({ data: { data } }) => {
      //       setVideo(data);
      //     })
      //     .catch((error) => {
      //       captureException(error);
      //       message.error('خطا هنگام آپلود!');
      //       clearFile();
      //     });
      // }
    },
    [video, httpClient, killUpload],
  );

  const pauseUpload = useCallback(() => {
    if (uploaderRef.current) {
      uploaderRef.current.pause();
    }
  }, []);

  const resumeUpload = useCallback(() => {
    if (!uploaderRef.current) {
      return;
    }
    try {
      uploaderRef.current.resume().catch((error) => {
        console.error('[UploadProvider] Error resuming upload:', error);
        captureException(error);
        message.error('خطا هنگام ادامه آپلود!');

        if (error.message === 'No file available for upload') {
          setTimeout(resumeUpload, 100);
        }
      });
    } catch (error) {
      console.error('[UploadProvider] Error in resumeUpload:', error);
      captureException(error);
      message.error('خطا هنگام ادامه آپلود!');
    }
  }, []);


  const value = useMemo(
    () => ({
      file,
      video,
      setVideo,
      clearFile: killUpload,
      onDrop,
      isUploading,
      isUploaded,
      progress,
      pauseUpload,
      resumeUpload,
      status,
      isActiveInAnotherTab,
      initialized: video?.s3?.parts,
    }),
    [
      file,
      video,
      progress,
      killUpload,
      isUploading,
      onDrop,
      pauseUpload,
      resumeUpload,
      isActiveInAnotherTab,
      isUploaded,
      status,
    ],
  );

  const isDesktop = useIsDesktop();
  const wakeLockRef = useRef(null);

  useEffect(() => {
    if (uploadState !== 'uploading') {
      return;
    }
    const beforeUnloadHandler = (event) => {
      event.preventDefault();
      event.returnValue = true;
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);
    return () => window.removeEventListener('beforeunload', beforeUnloadHandler);
  }, [uploadState]);

  useEffect(() => {
    if (!('wakeLock' in navigator) || uploadState !== 'uploading') {
      return;
    }

    const requestWakeLock = async () => {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        console.log('Screen Wake Lock: requested!');
      } catch (error) {
        console.error('Failed to request wake lock:', error);
        captureException(error);
        if (!isDesktop) {
          modal.warning({
            content: 'روشن بودن پاورسیوینگ ممکن از باعث وقفه در آپلود شود.',
          });
        }
      }
    };

    requestWakeLock();

    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release()
          .then(() => {
            console.log('Screen Wake Lock: released!');
            wakeLockRef.current = null;
          })
          .catch((error) => {
            console.error('Failed to release wake lock:', error);
            captureException(error);
          });
      }
    };
  }, [uploadState, isDesktop, modal]);

  return (
    <UploadContext.Provider value={value}>{children}</UploadContext.Provider>
  );
}

function UploadStatus() {
  const { status } = useContext(UploadContext);
  if (status === 'non_operational') {
    return (
      <Alert
        icon={<ExclamationCircleOutlined />}
        showIcon
        type="warning"
        title="آپلود غیرفعال است"
        description="درحال‌حاضر سرویس آپلود به‌صورت موقت غیرفعال است، لطفا بعدا تلاش کنید."
      />
    );
  }
  if (status === 'minor_incident') {
    return (
      <Alert
        closable
        icon={<ExclamationCircleOutlined />}
        showIcon
        type="error"
        title="اختلال در زیرساخت شبکه"
        description="درحال‌حاضر به علت وجود اختلال در زیرساخت شبکه احتمال ناموفق بودن آپلود وجود دارد، در صورت بروز مشکل لطفا بعدا تلاش کنید."
      />
    );
  }
  return null;
}

UploadProvider.UploadBox = UploadBox;
UploadProvider.UploadStatus = UploadStatus;
UploadProvider.useUploadingVideo = Uploader.useUploadingVideo;

export default UploadProvider;
